import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, ChevronDown, ChevronUp, ArrowRight, Loader2, CheckCircle, Users, TrendingUp, DollarSign, Award, Zap, Shield } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIERS = [
  {
    id: 'elite',
    name: 'Elite Athlete',
    tagline: 'For top-tier competitors',
    commission: '20%',
    color: '#FFD100',
    perks: [
      '20% commission on all merch sales',
      'Free product & apparel monthly',
      'Paid content & media opportunities',
      'Featured on homepage & social channels',
      'Custom discount code for your audience',
      'Priority event sponsorship & travel',
      'Nutrition coaching & performance consulting',
    ],
    requirements: [
      'Competitive athlete (regional/national/international)',
      '5,000+ engaged social media followers',
      'Consistent brand promotion (3+ posts/month)',
      'Active use of NinjAthlete products',
    ],
  },
  {
    id: 'ambassador',
    name: 'Brand Ambassador',
    tagline: 'For rising athletes & fitness creators',
    commission: '15%',
    color: '#E63946',
    perks: [
      '15% commission on referred sales',
      'Product discounts (up to 40% off)',
      'Paid photo & video shoot opportunities',
      'Personal brand promotion on our channels',
      'Exclusive ambassador-only gear',
      'Early access to new product launches',
    ],
    requirements: [
      'Active fitness enthusiast or athlete',
      '1,000+ social media followers',
      'Regular content creation (2+ posts/month)',
      'Genuine alignment with the NinjAthlete brand',
    ],
  },
  {
    id: 'peptide_affiliate',
    name: 'Peptide Research Affiliate',
    tagline: 'For biohackers, coaches & researchers',
    commission: '$25-75',
    color: '#00F0FF',
    perks: [
      '$25-75 per qualified referral to partner labs',
      'Custom affiliate tracking link & dashboard',
      'Access to exclusive peptide research content',
      'Co-branded educational materials',
      'Priority listing in our peptide resource directory',
      'Revenue share on PeptIQ app subscriptions (20% recurring)',
    ],
    requirements: [
      'Knowledge of peptide research & protocols',
      'Active audience (blog, YouTube, social, or newsletter)',
      'Commitment to educational, evidence-based content',
      'No medical claims or non-compliant marketing',
    ],
  },
];

const FAQ_ITEMS = [
  { q: 'How do I earn commissions?', a: 'You receive a unique tracking link and discount code. Any sale generated through your link earns you the commission rate for your tier. Commissions are paid monthly via PayPal or bank transfer.' },
  { q: 'When do I get paid?', a: 'Commissions are calculated on the 1st of each month and paid out by the 15th. There is a $50 minimum payout threshold.' },
  { q: 'Can I be in multiple tiers?', a: 'Yes! Many of our Elite Athletes also participate in the Peptide Research Affiliate program. Apply for the tier that best fits your primary focus and we can discuss adding additional programs.' },
  { q: 'How long does the application review take?', a: 'We review all applications within 5-7 business days. You will receive an email confirmation once your application has been received and another when a decision has been made.' },
  { q: 'What if I don\'t have a large following?', a: 'Follower count is just one factor. We value engagement, authenticity, and alignment with our brand values. If you have a small but highly engaged community, we want to hear from you.' },
  { q: 'Can I promote competitor products?', a: 'We ask that you do not actively promote direct competitors while representing NinjAthlete. We understand you may have other partnerships and are happy to discuss specifics during onboarding.' },
];

const FAQItem = ({ item }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-zinc-800 overflow-hidden mb-2">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-5 text-left hover:bg-zinc-900/50 transition-colors">
        <span className="text-white font-medium pr-4">{item.q}</span>
        {open ? <ChevronUp className="w-5 h-5 text-[#E63946] flex-shrink-0" /> : <ChevronDown className="w-5 h-5 text-zinc-500 flex-shrink-0" />}
      </button>
      {open && <div className="px-5 pb-5 text-zinc-400 text-sm leading-relaxed">{item.a}</div>}
    </div>
  );
};

export default function AffiliatePage() {
  const [selectedTier, setSelectedTier] = useState('ambassador');
  const [formData, setFormData] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    instagram: '', tiktok: '', youtube: '', twitter: '',
    sport_niche: '', follower_count: '', tier: 'ambassador',
    peptide_experience: '', why_partner: '', website: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'tier') setSelectedTier(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/affiliates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, tier: selectedTier }),
      });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.detail || 'Submission failed');
      }
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full px-4 py-3 bg-zinc-900 border border-zinc-700 text-white text-sm placeholder-zinc-500 focus:border-[#E63946] focus:outline-none transition-colors";

  return (
    <main className="pt-20 min-h-screen" data-testid="affiliate-page">
      {/* Hero */}
      <section className="relative overflow-hidden py-24 md:py-32">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0A] via-zinc-900 to-[#0A0A0A]" />
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,0.03) 35px, rgba(255,255,255,0.03) 70px)' }} />
        <div className="container-ninja relative z-10 text-center">
          <span className="inline-block px-4 py-1.5 text-xs uppercase tracking-widest bg-[#E63946] text-white font-bold mb-6">
            Partnership Program
          </span>
          <h1 className="text-4xl sm:text-5xl md:text-7xl text-white mb-6 leading-tight">
            Become a <span className="text-[#E63946]">NinjAthlete</span> Affiliate
          </h1>
          <p className="text-zinc-400 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed mb-10">
            Earn commissions promoting premium athletic gear and cutting-edge peptide research.
            Three tiers. Real revenue. No handouts.
          </p>
          <a href="#apply" className="btn-ninja px-8 py-4 text-lg font-heading tracking-wider inline-flex items-center gap-2">
            Apply Now <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-zinc-900/50 py-12 border-y border-zinc-800">
        <div className="container-ninja grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            { icon: Users, val: '200+', label: 'Active Affiliates' },
            { icon: DollarSign, val: '$150K+', label: 'Paid Out' },
            { icon: TrendingUp, val: '20%', label: 'Top Commission' },
            { icon: Award, val: '28', label: 'Products to Promote' },
          ].map(s => (
            <div key={s.label}>
              <s.icon className="w-6 h-6 text-[#E63946] mx-auto mb-2" />
              <div className="text-3xl text-white font-heading">{s.val}</div>
              <div className="text-zinc-500 text-xs uppercase tracking-wider mt-1">{s.label}</div>
            </div>
          ))}
        </div>
        <div className="container-ninja text-center mt-6">
          <Link to="/affiliates/leaderboard" className="inline-flex items-center gap-2 text-[#E63946] text-sm font-heading uppercase tracking-wider hover:text-white transition-colors" data-testid="affiliate-leaderboard-link">
            View Affiliate Leaderboard <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Philosophy */}
      <section className="container-ninja section-spacing">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-white text-3xl md:text-4xl mb-6">This Is a <span className="text-[#E63946]">Partnership</span>, Not a Handout</h2>
          <p className="text-zinc-400 leading-relaxed">
            A NinjAthlete sponsorship is earned through dedication, authenticity, and mutual investment.
            We don't just want athletes with followers — we want partners who live the brand.
            You promote us, we promote you. You grow, we grow with you.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: Zap, title: 'Who We Partner With', desc: 'Athletes, biohackers, coaches, and creators who embody peak performance. People who are genuine about their craft and inspire others daily.' },
            { icon: Shield, title: 'What We Expect', desc: 'Consistent brand representation, authentic content creation, and active engagement with your community. Quality over quantity, always.' },
            { icon: Award, title: 'What You Get', desc: 'Real commissions, free product, media opportunities, featured promotion, and a team that invests in your success as much as you invest in ours.' },
          ].map(item => (
            <div key={item.title} className="bg-zinc-900/50 border border-zinc-800 p-8 hover:border-[#E63946]/30 transition-colors">
              <item.icon className="w-8 h-8 text-[#E63946] mb-4" />
              <h3 className="text-white text-xl mb-3 font-heading">{item.title}</h3>
              <p className="text-zinc-400 text-sm leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Commission Tiers */}
      <section className="bg-zinc-900/30 py-20 border-y border-zinc-800" id="tiers">
        <div className="container-ninja">
          <div className="text-center mb-12">
            <h2 className="text-white text-3xl md:text-4xl mb-4">Commission <span className="text-[#E63946]">Tiers</span></h2>
            <p className="text-zinc-400">Choose the program that matches your profile</p>
          </div>
          <div className="grid lg:grid-cols-3 gap-6">
            {TIERS.map(tier => (
              <div
                key={tier.id}
                className={`relative bg-[#0A0A0A] border-2 p-8 transition-all duration-300 cursor-pointer hover:scale-[1.02] ${
                  selectedTier === tier.id ? 'border-[#E63946] shadow-lg shadow-[#E63946]/10' : 'border-zinc-800'
                }`}
                onClick={() => { setSelectedTier(tier.id); setFormData(prev => ({ ...prev, tier: tier.id })); }}
                data-testid={`tier-card-${tier.id}`}
              >
                {selectedTier === tier.id && (
                  <div className="absolute -top-3 left-6 px-3 py-0.5 bg-[#E63946] text-white text-xs font-bold uppercase tracking-wider">
                    Selected
                  </div>
                )}
                <div className="mb-6">
                  <h3 className="text-white text-2xl font-heading">{tier.name}</h3>
                  <p className="text-zinc-500 text-sm mt-1">{tier.tagline}</p>
                </div>
                <div className="mb-6">
                  <span className="text-4xl font-heading" style={{ color: tier.color }}>{tier.commission}</span>
                  <span className="text-zinc-500 text-sm ml-2">{tier.id === 'peptide_affiliate' ? 'per referral' : 'commission'}</span>
                </div>
                <div className="mb-6">
                  <p className="text-zinc-500 text-xs uppercase tracking-wider mb-3">What You Get</p>
                  {tier.perks.map(perk => (
                    <div key={perk} className="flex items-start gap-2 mb-2">
                      <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: tier.color }} />
                      <span className="text-zinc-300 text-sm">{perk}</span>
                    </div>
                  ))}
                </div>
                <div>
                  <p className="text-zinc-500 text-xs uppercase tracking-wider mb-3">Requirements</p>
                  {tier.requirements.map(req => (
                    <div key={req} className="flex items-start gap-2 mb-2">
                      <ArrowRight className="w-3 h-3 mt-1 text-zinc-600 flex-shrink-0" />
                      <span className="text-zinc-500 text-sm">{req}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="container-ninja section-spacing" id="apply">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-white text-3xl md:text-4xl mb-4">Apply <span className="text-[#E63946]">Now</span></h2>
            <p className="text-zinc-400">Tell us about yourself and why you'd be a great partner</p>
          </div>

          {success ? (
            <div className="text-center py-16 bg-zinc-900/50 border border-zinc-800" data-testid="affiliate-success">
              <CheckCircle className="w-16 h-16 text-[#E63946] mx-auto mb-4" />
              <h3 className="text-white text-2xl font-heading mb-2">Application Received!</h3>
              <p className="text-zinc-400 mb-6">We'll review your application and get back to you within 5-7 business days.</p>
              <Link to="/blog" className="btn-ninja px-6 py-3 inline-flex items-center gap-2">
                Explore Our Content <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="affiliate-form">
              {/* Tier Selection */}
              <div>
                <label className="text-zinc-500 text-xs uppercase tracking-wider mb-2 block">Program Tier *</label>
                <div className="grid grid-cols-3 gap-2">
                  {TIERS.map(tier => (
                    <button
                      key={tier.id}
                      type="button"
                      onClick={() => { setSelectedTier(tier.id); setFormData(prev => ({ ...prev, tier: tier.id })); }}
                      className={`py-3 px-2 text-sm font-heading tracking-wider text-center transition-all border ${
                        selectedTier === tier.id
                          ? 'bg-[#E63946]/10 border-[#E63946] text-white'
                          : 'bg-zinc-900 border-zinc-700 text-zinc-500 hover:border-zinc-500'
                      }`}
                      data-testid={`tier-btn-${tier.id}`}
                    >
                      {tier.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Personal Info */}
              <div className="grid grid-cols-2 gap-4">
                <input name="first_name" value={formData.first_name} onChange={handleChange} placeholder="First Name *" required className={inputClass} data-testid="aff-first-name" />
                <input name="last_name" value={formData.last_name} onChange={handleChange} placeholder="Last Name *" required className={inputClass} data-testid="aff-last-name" />
              </div>
              <input name="email" type="email" value={formData.email} onChange={handleChange} placeholder="Email *" required className={inputClass} data-testid="aff-email" />
              <div className="grid grid-cols-2 gap-4">
                <input name="phone" value={formData.phone} onChange={handleChange} placeholder="Phone" className={inputClass} />
                <input name="website" value={formData.website} onChange={handleChange} placeholder="Website / Blog URL" className={inputClass} />
              </div>

              {/* Social Media */}
              <div>
                <label className="text-zinc-500 text-xs uppercase tracking-wider mb-2 block">Social Media</label>
                <div className="grid grid-cols-2 gap-4">
                  <input name="instagram" value={formData.instagram} onChange={handleChange} placeholder="@instagram" className={inputClass} data-testid="aff-instagram" />
                  <input name="tiktok" value={formData.tiktok} onChange={handleChange} placeholder="@tiktok" className={inputClass} />
                  <input name="youtube" value={formData.youtube} onChange={handleChange} placeholder="YouTube channel" className={inputClass} />
                  <input name="twitter" value={formData.twitter} onChange={handleChange} placeholder="@twitter / X" className={inputClass} />
                </div>
              </div>

              {/* Details */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-zinc-500 text-xs uppercase tracking-wider mb-2 block">Sport / Niche *</label>
                  <input name="sport_niche" value={formData.sport_niche} onChange={handleChange} placeholder="e.g. CrossFit, HYROX, MMA" required className={inputClass} data-testid="aff-sport" />
                </div>
                <div>
                  <label className="text-zinc-500 text-xs uppercase tracking-wider mb-2 block">Total Followers</label>
                  <select name="follower_count" value={formData.follower_count} onChange={handleChange} className={inputClass}>
                    <option value="">Select range</option>
                    <option value="under-1k">Under 1,000</option>
                    <option value="1k-5k">1,000 - 5,000</option>
                    <option value="5k-25k">5,000 - 25,000</option>
                    <option value="25k-100k">25,000 - 100,000</option>
                    <option value="100k+">100,000+</option>
                  </select>
                </div>
              </div>

              {selectedTier === 'peptide_affiliate' && (
                <div>
                  <label className="text-zinc-500 text-xs uppercase tracking-wider mb-2 block">Peptide Experience</label>
                  <textarea name="peptide_experience" value={formData.peptide_experience} onChange={handleChange} placeholder="Describe your experience with peptide research, content creation, or related fields..." rows={3} className={inputClass} />
                </div>
              )}

              <div>
                <label className="text-zinc-500 text-xs uppercase tracking-wider mb-2 block">Why Should We Partner With You? *</label>
                <textarea name="why_partner" value={formData.why_partner} onChange={handleChange} placeholder="Tell us about yourself, your goals, and what you bring to the table..." required rows={4} className={inputClass} data-testid="aff-why" />
              </div>

              {error && <p className="text-red-500 text-sm" data-testid="affiliate-error">{error}</p>}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 bg-[#E63946] text-white font-heading text-lg uppercase tracking-wider flex items-center justify-center gap-3 hover:bg-[#E63946]/90 transition-colors disabled:opacity-50"
                data-testid="affiliate-submit"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><ArrowRight className="w-5 h-5" />Submit Application</>}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-zinc-900/30 py-20 border-t border-zinc-800">
        <div className="container-ninja max-w-2xl mx-auto">
          <h2 className="text-white text-3xl md:text-4xl text-center mb-10">Frequently Asked <span className="text-[#E63946]">Questions</span></h2>
          {FAQ_ITEMS.map(item => <FAQItem key={item.q} item={item} />)}
        </div>
      </section>
    </main>
  );
}
